import React from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import { Trip, getTrips } from '../../../services/trips/api/tripsApi';
import { formatBudget, formatDestinations, getFilteredTrips, normalizeStatus } from '../../../services/trips/utils/tripUtils';
import { formatTripDates } from '../../../services/trips/utils/dateUtils';
import { extractTripUuid } from '../../../services/trips/utils/idUtils';
import './TripsPage.css';

type TripStatus = 'in-progress' | 'drafting' | 'planned' | 'completed';

interface TripCard {
  id: string;
  title: string;
  dateRange: string;
  duration: string;
  imageUrl: string;
  status: TripStatus;
  locations: string[];
  budget?: string;
  stops: number;
  progress?: number;
  route?: string;
  sourceTrip: Trip;
}

const fallbackTripImages = [
  'https://images.unsplash.com/photo-1513735492246-483525079686?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1539037116277-4db20889f2d4?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=900&q=80',
];

const statusLabel: Record<TripStatus, string> = {
  'in-progress': 'In progress',
  drafting: 'Drafting',
  planned: 'Planned',
  completed: 'Completed',
};

const TripsPage: React.FC = () => {
  const navigate = useNavigate();
  const [trips, setTrips] = React.useState<Trip[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const fetchUserTrips = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const fetchedTrips = await getTrips();
      setTrips(fetchedTrips);
    } catch (err: any) {
      setError(err.message || 'Failed to load trips. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchUserTrips();
  }, [fetchUserTrips]);

  const openTrip = (selectedTrip: TripCard) => {
    navigate(`/trips/${selectedTrip.id}`, {
      state: { backTo: '/trips', owned: true, trip: selectedTrip.sourceTrip },
    });
  };

  const createNewTrip = () => {
    navigate('/trips/draft', {
      state: { backTo: '/trips', owned: true },
    });
  };

  const { drafted, inProgress, upcoming, past } = getFilteredTrips(trips);
  const inProgressCards = inProgress.map((trip, index) => mapTripToCard(trip, index));
  const plannedCards = [...drafted, ...upcoming].map((trip, index) => mapTripToCard(trip, index));
  const completedCards = past.map((trip, index) => mapTripToCard(trip, index));
  const heroTrip = inProgressCards[0];

  return (
    <DashboardLayout activeSection="trips" wide>
      <div className="v2-trips-page">
        <header className="v2-trips-page__header">
          <div>
            <p className="v2-trips-page__eyebrow">EnRoute · Trips</p>
            <h1>Your trips</h1>
          </div>
          <div className="v2-trips-page__actions">
            <button type="button" className="v2-trips-page__filter" onClick={fetchUserTrips}>Refresh</button>
            <button type="button" className="v2-trips-page__new" onClick={createNewTrip}>+ New trip</button>
          </div>
        </header>

        {isLoading && (
          <div className="v2-trips-page__state">
            <span className="v2-trips-page__spinner" />
            <p>Loading your trips...</p>
          </div>
        )}

        {!isLoading && error && (
          <div className="v2-trips-page__state v2-trips-page__state--error">
            <h2>Error loading trips</h2>
            <p>{error}</p>
            <button type="button" onClick={fetchUserTrips}>Retry</button>
          </div>
        )}

        {!isLoading && !error && (
          <>
        <div className="v2-trips-page__stats" aria-label="Trip status summary">
          <span><strong>{inProgressCards.length}</strong> In progress</span>
          <span><strong>{plannedCards.length}</strong> Planned</span>
          <span><strong>{completedCards.length}</strong> Completed</span>
        </div>

        {heroTrip && (
          <section className="v2-trips-section" aria-labelledby="in-progress-heading">
          <SectionHeading
            id="in-progress-heading"
            title="In progress"
            subtitle="Happening now"
            color="success"
          />
          <article className="v2-trips-page__hero-card">
            <div className="v2-trips-page__hero-image">
              <img src={heroTrip.imageUrl} alt="" />
              <span>IN PROGRESS</span>
            </div>
            <div className="v2-trips-page__hero-body">
              <p className="v2-trips-page__route">{heroTrip.route}</p>
              <h2>{heroTrip.title}</h2>
              <p className="v2-trips-page__dates">{heroTrip.dateRange} · {heroTrip.duration}</p>
              <div className="v2-trips-page__progress-row">
                <span>Trip progress</span>
                <strong>{heroTrip.progress || 0}%</strong>
              </div>
              <div className="v2-trips-page__progress" aria-label={`${heroTrip.progress || 0}% trip progress`}>
                <span style={{ width: `${heroTrip.progress || 0}%` }} />
              </div>
              <div className="v2-trips-page__hero-footer">
                <div>
                  <span>Next up</span>
                  <strong>{heroTrip.sourceTrip.activities?.[0] || 'Continue planning your next stop'}</strong>
                </div>
                <button type="button" onClick={() => openTrip(heroTrip)}>Open trip</button>
              </div>
            </div>
          </article>
        </section>
        )}

        <section className="v2-trips-section" aria-labelledby="planned-heading">
          <SectionHeading
            id="planned-heading"
            title="Planned"
            subtitle="Still shaping these"
            color="info"
          />
          <div className="v2-trips-page__grid">
            {plannedCards.map((trip) => (
              <TripPreviewCard key={trip.id} trip={trip} onOpen={openTrip} />
            ))}
          </div>
          {plannedCards.length === 0 && <EmptyTripsMessage message="No planned trips yet." />}
        </section>

        <section className="v2-trips-section" aria-labelledby="completed-heading">
          <SectionHeading
            id="completed-heading"
            title="Completed"
            subtitle="Your travel history"
            color="muted"
          />
          <div className="v2-trips-page__grid">
            {completedCards.map((trip) => (
              <TripPreviewCard key={trip.id} trip={trip} compact onOpen={openTrip} />
            ))}
          </div>
          {completedCards.length === 0 && <EmptyTripsMessage message="No completed trips yet." />}
        </section>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

const mapTripToCard = (trip: Trip, index = 0): TripCard => {
  const status = mapTripStatus(trip.status);
  const destinations = trip.destinations || [];
  const budget = formatBudget(trip.budget);
  const uuid = extractTripUuid(trip.id);
  const image = trip.images?.find((item) => /^https?:\/\//.test(item));

  return {
    id: uuid,
    title: trip.title,
    dateRange: formatTripDates(trip.dates),
    duration: getTripDuration(trip),
    imageUrl: image || fallbackTripImages[index % fallbackTripImages.length],
    status,
    locations: destinations,
    budget: budget ? `${budget} budget` : undefined,
    stops: destinations.length,
    progress: trip.progress || 0,
    route: formatDestinations(destinations),
    sourceTrip: trip,
  };
};

const mapTripStatus = (status: string): TripStatus => {
  const normalized = normalizeStatus(status);

  if (normalized === 'in_progress') return 'in-progress';
  if (normalized === 'completed') return 'completed';
  if (normalized === 'draft') return 'drafting';
  return 'planned';
};

const getTripDuration = (trip: Trip) => {
  if (!trip.dates?.startDate || !trip.dates?.endDate) return 'Dates TBD';

  const start = new Date(trip.dates.startDate);
  const end = new Date(trip.dates.endDate);
  const diff = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  if (!Number.isFinite(diff) || diff <= 0) {
    return 'Dates TBD';
  }

  return `${diff} ${diff === 1 ? 'day' : 'days'}`;
};

const EmptyTripsMessage: React.FC<{ message: string }> = ({ message }) => (
  <div className="v2-trips-page__empty">
    <p>{message}</p>
  </div>
);

interface SectionHeadingProps {
  id: string;
  title: string;
  subtitle: string;
  color: 'success' | 'info' | 'muted';
}

const SectionHeading: React.FC<SectionHeadingProps> = ({ id, title, subtitle, color }) => (
  <div className="v2-trips-section__heading">
    <span className={`v2-trips-section__dot v2-trips-section__dot--${color}`} />
    <h2 id={id}>{title}</h2>
    <p>{subtitle}</p>
  </div>
);

interface TripPreviewCardProps {
  trip: TripCard;
  compact?: boolean;
  onOpen: (trip: TripCard) => void;
}

const TripPreviewCard: React.FC<TripPreviewCardProps> = ({ trip, compact = false, onOpen }) => (
  <article
    className={`v2-trip-card ${compact ? 'v2-trip-card--compact' : ''}`}
    onClick={() => onOpen(trip)}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onOpen(trip);
      }
    }}
    role="button"
    tabIndex={0}
  >
    <div className="v2-trip-card__media">
      <img src={trip.imageUrl} alt="" />
      <span className={`v2-trip-card__status v2-trip-card__status--${trip.status}`}>
        {statusLabel[trip.status]}
      </span>
      <div className="v2-trip-card__locations">
        {trip.locations.map((location) => (
          <span key={location}>{location}</span>
        ))}
      </div>
    </div>
    <div className="v2-trip-card__body">
      <h3>{trip.title}</h3>
      <p>{trip.dateRange} · {trip.duration}</p>
      <div className="v2-trip-card__footer">
        <span>{trip.budget || 'Drafting in chat'}</span>
        <span>{trip.stops} stops</span>
      </div>
    </div>
  </article>
);

export default TripsPage;
